import { BoxItem } from 'box-ui-elements/es';
import { FileType } from 'enums';
import { SubmissionMarket, UpdateSubmissionMarketRequest } from 'types';
import { INTERWEST_MARKETS } from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/markets/interwest-markets';
import {
  getDeclinedMarketNames,
  getDeferredMarketNames,
  getQuotingMarketNames,
} from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/markets/utils';
import {
  createAnalyzeQuotesStep,
  createAutomatedRenewalPolicyStep,
  createDataImportedFromAMSStep,
  createDeclinationStep,
  createDeferredToUnderwriterStep,
  createEmailMarketingStep,
  createPortalMarketingStep,
  createProposalCreationStep,
  createQuotesReceivedStep,
  createRemarketingDecisionStep,
  createReviewProposal,
  DemoStep,
  FetchingAccountInformationStep,
  WritingInformationBackToAMS,
} from 'broker/pages/SubmissionWorkspacePage/components/Header/RenewalDemo/steps/shared-steps';
import { EmailListItemAttributes } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/components/Email/utils';

interface InterwestDemoStepsParams {
  submissionId: string;
  markets: SubmissionMarket[];
  reFetchWorkspace: () => Promise<void>;
  updateSubmissionMarketRequest: (
    id: string,
    data: UpdateSubmissionMarketRequest,
    skipRefetch?: boolean,
  ) => Promise<void>;
  fetchFromAMS: (submissionId: string) => Promise<void | null>;
  marketViaEmail: (submissionId: string) => Promise<void | null>;
  marketViaPortal: (submissionId: string) => Promise<void | null>;
  receiveQuotes: (params: {
    submissionId: string;
    quoteType?: FileType.MarketQuote | FileType.RenewalPolicy;
  }) => Promise<void | null>;
  createProposal: (submissionId: string) => Promise<void | null>;
  reFetchFileItems: () => Promise<void>;
  reFetchSubmissionEmails: () => Promise<void>;
  expiringPolicy?: BoxItem;
  renewalPolicy?: BoxItem;
  application?: BoxItem;
  marketQuotes: Array<BoxItem>;
  marketingEmails: EmailListItemAttributes[];
  proposalFile?: BoxItem;
  onClose: () => void;
  navigateToProposal: () => void;
}

export function createInterwestDemoSteps(params: InterwestDemoStepsParams): DemoStep[] {
  const {
    submissionId,
    markets,
    reFetchWorkspace,
    updateSubmissionMarketRequest,
    fetchFromAMS,
    marketViaEmail,
    marketViaPortal,
    receiveQuotes,
    createProposal,
    reFetchFileItems,
    reFetchSubmissionEmails,
    expiringPolicy,
    renewalPolicy,
    application,
    marketQuotes,
    marketingEmails,
    proposalFile,
    onClose,
    navigateToProposal,
  } = params;

  const declinedPortalMarketNames = getDeclinedMarketNames(INTERWEST_MARKETS, 'portal');
  const quotingMarketNames = getQuotingMarketNames(INTERWEST_MARKETS);
  const deferredMarketNames = getDeferredMarketNames(INTERWEST_MARKETS);

  return [
    FetchingAccountInformationStep,

    createDataImportedFromAMSStep({
      expiringPolicy,
      application,
      onClose,
      fetchFromAMS,
      reFetchFileItems,
      reFetchWorkspace,
      submissionId,
    }),

    createAutomatedRenewalPolicyStep({
      renewalPolicy,
      onClose,
      receiveQuotes,
      reFetchFileItems,
      reFetchWorkspace,
      submissionId,
    }),

    createRemarketingDecisionStep({
      expiringPolicy,
      renewalPolicy,
      onClose,
    }),

    createEmailMarketingStep({
      markets: INTERWEST_MARKETS,
      marketViaEmail,
      reFetchSubmissionEmails,
      reFetchWorkspace,
      onClose,
      marketingEmails,
      submissionId,
    }),

    createPortalMarketingStep({
      markets: INTERWEST_MARKETS,
      marketViaPortal,
      reFetchWorkspace,
      submissionId,
      videoUrl: 'https://s3.us-west-2.amazonaws.com/demo.dev.capitola-ins.com/interwest-portal-demo.mov',
    }),

    createDeclinationStep({
      declinedMarketNames: declinedPortalMarketNames,
      markets,
      updateSubmissionMarketRequest,
    }),

    createDeferredToUnderwriterStep({
      deferredMarketNames,
      markets,
      updateSubmissionMarketRequest,
    }),

    createQuotesReceivedStep({
      quotingMarketNames,
      marketQuotes,
      receiveQuotes,
      reFetchFileItems,
      reFetchWorkspace,
      onClose,
      submissionId,
    }),

    createAnalyzeQuotesStep({
      markets: INTERWEST_MARKETS,
      marketQuotes: renewalPolicy ? [...marketQuotes, renewalPolicy] : marketQuotes,
      onClose,
    }),

    createProposalCreationStep({
      createProposal,
      reFetchFileItems,
      reFetchWorkspace,
      submissionId,
      proposalFile,
      onClose,
    }),

    createReviewProposal({
      navigateToProposal,
    }),

    WritingInformationBackToAMS,
  ];
}
